import React from 'react'
import { useIntl } from 'react-intl'
import { BasicDialogBody, Dialog } from '../../legacy-design-system/modal'
import { ExclusionFormManager } from './exclusion-form-manager'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { css } from '@emotion/css'
import { ExclusionForm } from './exclusion-form'
import {
  createApplicationExclusion,
  CreateAppExclusionArgs,
  CreateAppExclusionResponse
} from './api/requests/create-application-exclusion'
import { useContrastMutation } from '../../hooks/use-contrast-mutation'
import { logSuccess, logError } from '../toaster'
import {
  AttackEventForExclusion,
  getErrorMsg,
  getExclusionError,
  initDefaultInputExclusion,
  initExclusionFromAttackEvent,
  isBadRequest,
  isDuplicateRequest,
  isFormInvalid,
  normalizeExclusionForm
} from './shared'
import { AgentLanguageValue } from '../agent-language'
import { HTTPError } from '../../api/fetch/decorate-fetch'
import { FormValidator } from '../../legacy-design-system/form'
import { IconExternalLink } from '@contrast-security-inc/design-system-foundations'
import { getApplicationExclusionsRoute } from '../route'

export const testIds = {
  dialog: 'add-exclusion-dialog',
  addButton: 'add-exclusion-button',
  footerLink: 'add-exclusion-footer-link'
}

interface AddExclusionDialogProps {
  isOpen: boolean
  organizationUuid: string
  applicationId: string
  applicationName: string
  applicationLanguage: AgentLanguageValue
  attackEvent?: AttackEventForExclusion
  isAssessFeatureEnabled: boolean
  isProtectFeatureEnabled: boolean
  successCallback: (exclusionId: string, shouldSuppressEvents: boolean) => void
  closeDialog: () => void
  shouldAllowSuppressEvents?: boolean
}

export function AddExclusionDialog({
  isOpen,
  closeDialog,
  organizationUuid,
  applicationId,
  applicationName,
  applicationLanguage,
  attackEvent,
  successCallback,
  isAssessFeatureEnabled,
  isProtectFeatureEnabled,
  shouldAllowSuppressEvents = false
}: AddExclusionDialogProps) {
  const intl = useIntl()
  const { mutate: addExclusion } = useContrastMutation<
    CreateAppExclusionResponse,
    HTTPError,
    CreateAppExclusionArgs
  >(createApplicationExclusion)

  return (
    <ExclusionFormManager
      exclusion={
        attackEvent
          ? initExclusionFromAttackEvent(
              attackEvent.ruleUuid,
              attackEvent.userInput,
              attackEvent.sourceIP,
              attackEvent.url
            )
          : initDefaultInputExclusion()
      }
      organizationUuid={organizationUuid}
      language={applicationLanguage}
      attackEvent={attackEvent}
      shouldAllowSuppressEvents={shouldAllowSuppressEvents}
      isAssessFeatureEnabled={isAssessFeatureEnabled}
      isProtectFeatureEnabled={isProtectFeatureEnabled}
    >
      {({
        exclusion,
        exclusionError,
        shouldSuppressEvents,
        commonRules,
        protectOnlyRules,
        assessOnlyRules,
        selectedRules,
        setExclusion,
        setExclusionError,
        setSelectedRules,
        toggleShouldSuppressEvents
      }) => {
        return (
          <FormValidator value={exclusion}>
            {({ getFormFieldProps, formIsValid }) => {
              const descriptionMsg = intl.formatMessage(
                {
                  id: 'detail_appExclusions_add'
                },
                {
                  a: (...msg) => (
                    <Link
                      isExternal
                      font="metadata"
                      href={intl.formatMessage({
                        id: 'detail_appExclusions_docLink'
                      })}
                    >
                      {msg}
                    </Link>
                  )
                }
              )

              return (
                <Dialog
                  testId={testIds.dialog}
                  size="large"
                  isOpen={isOpen}
                  onCancel={closeDialog}
                  renderTitle={() => (
                    <>
                      <Text
                        font="bodyStrong"
                        color="neutral100"
                        className={css`
                          margin-right: 5px;
                        `}
                      >
                        {intl.formatMessage({ id: 'title_addExclusion' })}
                      </Text>
                      <Text font="body" color="neutral60">
                        ({applicationName})
                      </Text>
                    </>
                  )}
                  renderDescription={() => descriptionMsg}
                >
                  <BasicDialogBody
                    disableConfirmButton={isFormInvalid(
                      formIsValid,
                      exclusion.exclusionName
                    )}
                    body={
                      <ExclusionForm
                        exclusion={exclusion}
                        exclusionError={exclusionError}
                        shouldSuppressEvents={shouldSuppressEvents}
                        shouldAllowSuppressEvents={shouldAllowSuppressEvents}
                        commonRules={commonRules}
                        assessOnlyRules={assessOnlyRules}
                        protectOnlyRules={protectOnlyRules}
                        selectedRules={selectedRules}
                        hasAttackEvent={!!attackEvent}
                        applicationLanguage={applicationLanguage}
                        getFormFieldProps={getFormFieldProps}
                        setExclusion={setExclusion}
                        setSelectedRules={setSelectedRules}
                        toggleShouldSuppressEvents={toggleShouldSuppressEvents}
                      />
                    }
                    onConfirm={() => {
                      addExclusion(
                        {
                          organizationUuid,
                          applicationId,
                          exclusion: normalizeExclusionForm(
                            exclusion,
                            selectedRules,
                            !!attackEvent ? attackEvent.uuid : undefined
                          )
                        },
                        {
                          onSuccess: ({ messages, exclusionId }) => {
                            setExclusionError({})
                            logSuccess(messages[0])
                            closeDialog()
                            successCallback(exclusionId, shouldSuppressEvents)
                          },
                          onError: ({ data: { messages, errors }, status }) => {
                            logError(getErrorMsg(messages))

                            if (
                              isBadRequest(status) &&
                              Array.isArray(errors) &&
                              errors.length > 0
                            ) {
                              setExclusionError(getExclusionError(errors))
                            } else if (isDuplicateRequest(status)) {
                              closeDialog()
                            }
                          }
                        }
                      )
                    }}
                    onCancel={closeDialog}
                    confirmButtonText={
                      <Text font="body" testId={testIds.addButton}>
                        {intl.formatMessage({ id: 'action_add' })}
                      </Text>
                    }
                    cancelButtonText={
                      <Text font="body">
                        {intl.formatMessage({ id: 'action_cancel' })}
                      </Text>
                    }
                    footerChildren={
                      attackEvent && (
                        <span
                          className={css`
                            display: flex;
                            align-items: center;
                          `}
                        >
                          <IconExternalLink size="sm" color="neutral70" />
                          <Link
                            testId={testIds.footerLink}
                            isExternal
                            href={getApplicationExclusionsRoute(
                              organizationUuid,
                              applicationId
                            )}
                            font="body"
                            className={css`
                              margin-left: 8px;
                            `}
                          >
                            {intl.formatMessage({
                              id: 'action_manageAllExclusions'
                            })}
                          </Link>
                        </span>
                      )
                    }
                  />
                </Dialog>
              )
            }}
          </FormValidator>
        )
      }}
    </ExclusionFormManager>
  )
}
